import axios, { type InternalAxiosRequestConfig } from 'axios';
import { useVendorStore } from '@/store/vendor-store';

// ─── Axios instance ───────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export const http = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Auth token injection ─────────────────────────────────────────────────────

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('vendor_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── 401 handling: silent refresh for expired sessions, never for auth calls ───

let isRefreshing = false;

// Auth endpoints own their own error UX (the login form shows "invalid
// credentials", forgot-password shows its message). The interceptor must NOT
// hijack their 401s — doing so previously cleared storage and hard-redirected
// to /login, reloading the page so fast the inline error was never visible.
function isAuthCall(url: unknown): boolean {
  return typeof url === 'string' && /\/auth\/(login|refresh|register|forgot-password|reset-password)/.test(url);
}

function forceLogout() {
  // Clear auth state through the store so the persisted 'vendor-auth' snapshot
  // (isAuthenticated) is reset too — not just the raw token keys.
  try { useVendorStore.getState().logout(); } catch { /* store not ready */ }
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    // Let login / refresh / forgot-password surface their own errors.
    if (isAuthCall(original?.url)) return Promise.reject(error);

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;

      const refreshToken = localStorage.getItem('vendor_refresh_token');
      if (!refreshToken || isRefreshing) {
        forceLogout();
        return Promise.reject(error);
      }

      isRefreshing = true;
      try {
        const res = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefresh } = res.data.data;
        localStorage.setItem('vendor_token', accessToken);
        if (newRefresh) localStorage.setItem('vendor_refresh_token', newRefresh);
        if (original.headers) original.headers.Authorization = `Bearer ${accessToken}`;
        return http(original);
      } catch {
        forceLogout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ─── Vendor API calls ─────────────────────────────────────────────────────────

type QueryParams = Record<string, string | number | boolean | undefined>;

export const vendorApi = {
  login: (email: string, password: string) =>
    http.post('/auth/login', { identifier: email, password }),

  logout: () => http.post('/auth/logout'),

  getMe: () => http.get('/auth/me'),

  // Vendor self
  getMyVendor: () => http.get('/vendors/my-vendor'),
  getVendor: (id: string) => http.get(`/vendors/${id}`),
  updateVendor: (id: string, data: Record<string, unknown>) => http.put(`/vendors/${id}`, data),
  registerVendor: (data: Record<string, unknown>) => http.post('/vendors/register', data),
  getVendorStats: (id: string) => http.get(`/vendors/${id}/stats`),

  // Bank accounts (vendor self-service)
  getMyBankAccounts: () => http.get('/vendors/me/bank-accounts'),
  addMyBankAccount: (data: Record<string, unknown>) =>
    http.post('/vendors/me/bank-accounts', data),
  updateMyBankAccount: (accountId: string, data: Record<string, unknown>) =>
    http.put(`/vendors/me/bank-accounts/${accountId}`, data),
  removeMyBankAccount: (accountId: string) =>
    http.delete(`/vendors/me/bank-accounts/${accountId}`),
  setDefaultMyBankAccount: (accountId: string) =>
    http.patch(`/vendors/me/bank-accounts/${accountId}/default`),

  // Products — vendor uses /products with JWT role guard
  getProducts: (params?: QueryParams) => http.get('/products', { params }),

  getProduct: (id: string) => http.get(`/products/${id}`),

  createProduct: (data: Record<string, unknown>) => http.post('/products', data),

  updateProduct: (id: string, data: Record<string, unknown>) =>
    http.put(`/products/${id}`, data),

  deleteProduct: (id: string) => http.delete(`/products/${id}`),

  publishProduct: (id: string) => http.patch(`/products/${id}/publish`),

  // Categories — public
  getCategoryTree: () => http.get('/categories/tree'),
  getCategories: () => http.get('/categories'),

  // Orders
  getOrders: (params?: QueryParams) => http.get('/orders/vendor', { params }),
  getOrder: (id: string) => http.get(`/orders/${id}`),
  updateOrderStatus: (id: string, status: string, note?: string) =>
    http.patch(`/orders/${id}/status`, { status, note }),
  addTracking: (id: string, trackingNumber: string, courierName: string) =>
    http.patch(`/orders/${id}/tracking`, { trackingNumber, courierName }),

  // Analytics
  getAnalytics: (vendorId: string) => http.get(`/analytics/vendor/${vendorId}/my`),

  // Wallet / Finance (per logged-in user)
  getWalletBalance: () => http.get('/wallet/balance'),
  getWalletTransactions: (params?: QueryParams) =>
    http.get('/wallet/transactions', { params }),
  getPayouts: (params?: QueryParams) => http.get('/wallet/payouts', { params }),
  requestPayout: (amount: number, bankDetails: Record<string, unknown>) =>
    http.post('/wallet/payout', { amount, bankDetails }),

  // Reviews
  getReviews: (vendorId: string, params?: QueryParams) =>
    http.get(`/reviews/vendor/${vendorId}`, { params }),
  replyToReview: (reviewId: string, reply: string) =>
    http.patch(`/reviews/${reviewId}/vendor-reply`, { reply }),

  // Uploads
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<{ data: { url: string; filename: string } }>('/uploads/image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadBanner: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<{ data: { url: string; filename: string } }>('/uploads/banner', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadKycDocument: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<{ data: { url: string; key: string; filename: string } }>('/uploads/kyc', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Resolve a stored KYC doc key into a short-lived, viewable URL (the file
  // lives in the private MinIO bucket and is streamed through the authenticated
  // API). Returns '' if not available.
  getKycViewUrl: async (vendorId: string, doc: string): Promise<string> => {
    const res = await http.get('/uploads/kyc/url', { params: { vendorId, doc } });
    const data = (res.data?.data ?? res.data) as { token?: string; url?: string };
    if (data?.token) return `${API_BASE}/uploads/kyc/view?token=${encodeURIComponent(data.token)}`;
    return data?.url ?? '';
  },
};
